// third party imports
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { ClockCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";

// internal rebet imports
import "./stylesHeader.css";

// component imports
import AppContext from "../../../context/AppContext";

import { formatTimezoneDisplay } from "../../../utils/dateService";
import { clearAuthAndRedirect } from "../../../utils/tokenUtils";
import { useSignOut } from "../../../queries/auth/useSignOut";
import {
  getStoredAdminLanguage,
  setStoredAdminLanguage,
  setStoredGuestLanguage,
} from "../../../utils/i18n/languagePreference";
import { canonicalizeLanguageTag } from "../../../utils/i18n/supportedLanguages";
import { canonicalizeAvailableLanguages } from "../../../utils/i18n/availableLanguages";
import { useUpdateStaffPreferences } from "../../../queries/useUpdateStaffPreferences";
import { LanguageSelect } from "../../shared";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { useCurrentPageTitle } from "../Layout/PageTitleContext";

const isEmailAddress = (value) =>
  typeof value === "string" && value.includes("@");

const Header = () => {
  // non-state variables
  const appConfig = useSelector((state) => state.config.appConfig);
  const {
    adminEmail,
    adminUsername,
    adminRoleLabel,
    adminTimezone,
    adminLanguage,
    isExpanded,
  } = useContext(AppContext) || {};
  const signOut = useSignOut();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const profileMenuRef = useRef(null);

  const updateStaffPreferences = useUpdateStaffPreferences();
  const pageTitle = useCurrentPageTitle();

  const email = useMemo(() => {
    try {
      const storedEmail = localStorage.getItem("email");
      const parsedEmail = storedEmail ? JSON.parse(storedEmail) : null;
      if (isEmailAddress(adminEmail)) {
        return adminEmail;
      }

      if (isEmailAddress(parsedEmail)) {
        return parsedEmail;
      }

      return adminUsername || "";
    } catch {
      if (isEmailAddress(adminEmail)) {
        return adminEmail;
      }

      const storedEmail = localStorage.getItem("email");
      if (isEmailAddress(storedEmail)) {
        return storedEmail;
      }

      return adminUsername || "";
    }
  }, [adminEmail, adminUsername]);

  const userInitial = email?.charAt(0)?.toUpperCase() || "?";
  const timezoneDisplay = useMemo(
    () => formatTimezoneDisplay(adminTimezone),
    [adminTimezone],
  );

  const activeLanguage =
    canonicalizeLanguageTag(getStoredAdminLanguage()) ??
    canonicalizeLanguageTag(adminLanguage) ??
    "en";
  const languageOptions = useMemo(() => {
    const options = canonicalizeAvailableLanguages(
      appConfig?.localization?.available_languages,
    );
    // Always list the active language so the select can render its value even
    // if the backend availability list omits it.
    return options.includes(activeLanguage)
      ? options
      : [activeLanguage, ...options];
  }, [appConfig?.localization?.available_languages, activeLanguage]);
  const primaryCurrencyShortcode =
    appConfig?.app_currency_config?.primary_currency?.shortcode?.trim();

  // state variables

  // use effects'
  useEffect(() => {
    if (!isProfileMenuOpen) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (!profileMenuRef.current?.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsProfileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isProfileMenuOpen]);

  useEffect(() => {
    const contentContainer = document.querySelector(".contentContainer");
    const updateScrolledState = () => {
      setIsScrolled(
        window.scrollY > 8 || (contentContainer?.scrollTop ?? 0) > 8,
      );
    };

    updateScrolledState();
    window.addEventListener("scroll", updateScrolledState, { passive: true });
    contentContainer?.addEventListener("scroll", updateScrolledState, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", updateScrolledState);
      contentContainer?.removeEventListener("scroll", updateScrolledState);
    };
  }, []);

  // api functions

  // helper functions
  const handleSignOutClicked = () => {
    signOut.mutate(undefined, {
      onSettled: () => {
        clearAuthAndRedirect();
      },
    });
  };

  // antd Select passes the selected value directly (not a DOM event).
  const handleLanguageChange = (lng) => {
    if (!lng || lng === activeLanguage) return;

    updateStaffPreferences.mutate(
      { language: lng },
      {
        // Runs for both the persisted path and the 404 feature-detection path
        // (endpoint not deployed → localStorage-only, no user-facing error).
        onSuccess: () => {
          setStoredAdminLanguage(lng);
          // Keep the guest key in sync so logout still shows this language
          // on the login screen (guest wins boot/pre-auth resolution).
          setStoredGuestLanguage(lng);
          // Apply mechanism v1: full reload re-keys i18next + AppStrings from
          // the stored preference. No live in-session switching.
          window.location.reload();
        },
        onError: (error) => {
          // eslint-disable-next-line no-console
          console.error("Failed to update language preference:", error);
        },
      },
    );
  };

  const languageSelectProps = {
    value: activeLanguage,
    onChange: handleLanguageChange,
    disabled: updateStaffPreferences.isPending,
    options: languageOptions,
  };

  return (
    <div
      className={[
        "header",
        isExpanded ? "expanded" : "collapsed",
        isScrolled ? "header--scrolled" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      data-testid="app-header"
    >
      {pageTitle ? (
        <h1 className="header-page-title" data-testid="header-page-title">
          {pageTitle}
        </h1>
      ) : null}
      <div className="header-actions">
        {primaryCurrencyShortcode ? (
          <Tooltip title={AppStrings.PAM_PRIMARY_CURRENCY_SHORTCODE_TOOLTIP}>
            <span
              className="header-primary-currency-shortcode"
              data-testid="header-primary-currency-shortcode"
            >
              {primaryCurrencyShortcode}
            </span>
          </Tooltip>
        ) : null}
        <LanguageSelect
          className="header-language-select"
          data-testid="header-language-select"
          {...languageSelectProps}
        />

        <div className="header-profile-container" ref={profileMenuRef}>
          <button
            className="header-profile-trigger"
            type="button"
            aria-label="Open account menu"
            aria-expanded={isProfileMenuOpen}
            onClick={() =>
              setIsProfileMenuOpen((previousValue) => !previousValue)
            }
          >
            <div className="header-profile-avatar" aria-hidden="true">
              <span className="header-profile-avatar-text">{userInitial}</span>
            </div>
            <span className="header-profile-email">
              {email || "Unknown user"}
            </span>
          </button>

          {isProfileMenuOpen ? (
            <div
              className="header-profile-popover"
              role="dialog"
              aria-label="Account menu"
            >
              <div
                className="header-profile-popover-header"
                data-testid="header-profile-popover-header"
              >
                <div className="header-profile-meta">
                  <p className="header-profile-popover-email">
                    {email || "Unknown user"}
                  </p>
                  <p className="header-profile-popover-role">
                    {adminRoleLabel || "PAM User"}
                  </p>
                </div>
                <LanguageSelect
                  className="header-profile-language-select"
                  data-testid="header-profile-language-select"
                  {...languageSelectProps}
                />
              </div>

              <div className="header-profile-popover-row">
                <span
                  className="header-profile-popover-icon"
                  aria-hidden="true"
                >
                  <ClockCircleOutlined />
                </span>
                <span
                  className="header-profile-popover-value"
                  title={timezoneDisplay}
                >
                  {timezoneDisplay}
                </span>
              </div>

              <button
                className="header-profile-signout-button"
                type="button"
                onClick={handleSignOutClicked}
                disabled={signOut.isPending}
                aria-busy={signOut.isPending ? "true" : "false"}
              >
                {signOut.isPending && <LoadingOutlined spin aria-hidden="true" />}
                {signOut.isPending
                  ? AppStrings.PAM_SIGNING_OUT
                  : AppStrings.SIGN_OUT}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default Header;
